import type { BuiltInTool, BuiltInToolError } from './types'

/** Every planning tool reads across the whole enterprise, so a run whose task is not OS-owned is refused. */
const OS_OWNED_ONLY: BuiltInToolError = {
    code: 'os_owned_only',
    when: "The run's task is not OS-owned. These tools read every team's work, so only an OS task may call them.",
    handling: 'tool_error',
}

/**
 * Tools a planning task (#219) uses to read the whole enterprise before it proposes anything, and to write and read
 * back the plan the CEO signs off: who is on which team, what every task does, which tool types are connected, what
 * the catalog already has, and what a deletion would break. A run calls them: the run dispatcher routes each one to
 * `OsPlanningTools` (apps/api/src/worker/os-planning-tools.ts), which answers it with no vendor binding.
 */
export const OS_PLANNING_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.read_enterprise',
        family: 'os.planning',
        summary: 'The whole enterprise: teams, charters, roles, members and every task in full.',
        description:
            'Read the whole enterprise a plan is proposed against: every team with its charter, roles and members (persona and roles), the members on no team, and every task, OS-owned ones included, in full: goal, metric, guardrails, tools, gates, spend, successors, finishesOn (the outcomes a run ends on that finish its work), its assigneeRole and the members holding it (roleHolderIds; assigneeMemberId when exactly one does), teamIds (the teams that inherit it: each team where a member holds its role, seated or as lead), and lastRunStatus. A task is assigned to a role, never pinned to a member and never placed on a team: a team runs the tasks of the roles its members hold. With no arguments it returns everything. When that is too big to hand back (tool_result_truncated), read it in slices, each with its tasks in full: noTeam: true for the members on no team and the tasks whose role no team holds (OS-owned tasks among them), then team (a team id or slug) for each team, with its roles, members and the tasks of the roles its members hold. A task whose role is held on several teams is in each of their slices. When one team is too big, read it role by role: team with role (a role id or slug from allRoles in that team slice) for its tasks that run as that role, and team with noRole: true for its tasks that run as no role. Every slice lists allTeams, the id and slug of every team; every team slice also lists allRoles, every role its tasks can run as. Together the slices cover every member and task. OS-owned tasks only.',
        inputSchema: {
            type: 'object',
            properties: {
                team: {
                    type: 'string',
                    description:
                        'A team id or slug: that team, its roles, members and the tasks of the roles its members hold.',
                },
                noTeam: {
                    type: 'boolean',
                    description: 'true: the members on no team and the tasks whose role no team holds.',
                },
                role: {
                    type: 'string',
                    description: "With team only. A role id or slug from allRoles: that team's tasks that run as it.",
                },
                noRole: {
                    type: 'boolean',
                    description: "With team only. true: that team's tasks that run as no role.",
                },
            },
            additionalProperties: false,
        },
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        errors: [
            OS_OWNED_ONLY,
            {
                code: 'team_not_found:<team>',
                when: 'No team in this enterprise has that id or slug. Read allTeams with noTeam: true.',
                handling: 'tool_error',
            },
            { code: 'team_selector_conflict', when: 'Both `team` and `noTeam` are set.', handling: 'tool_error' },
            {
                code: 'role_requires_team',
                when: '`role` or `noRole` is set without `team`: they narrow one team.',
                handling: 'tool_error',
            },
            {
                code: 'role_not_found:<role>',
                when: "No task on that team can run as a role with that id or slug. The message lists the team's allRoles.",
                handling: 'tool_error',
            },
            { code: 'role_selector_conflict', when: 'Both `role` and `noRole` are set.', handling: 'tool_error' },
        ],
        examples: [
            { input: {} },
            { input: { noTeam: true } },
            { input: { team: 'engineering' } },
            { input: { team: 'engineering', role: 'cto' } },
            { input: { team: 'engineering', noRole: true } },
        ],
        public: true,
    },
    {
        name: 'os.search_catalog',
        family: 'os.planning',
        summary: 'Published catalog tasks that match a plain-English intent, best first.',
        description:
            'Find published catalog tasks that match a plain-English intent, best match first (at most 10), so a plan proposes something proven instead of inventing it. Scored on the words of the intent found in a task slug or name; an intent of only filler words matches nothing. Pass intent.',
        inputSchema: {
            type: 'object',
            properties: { intent: { type: 'string' } },
            required: ['intent'],
            additionalProperties: false,
        },
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        errors: [OS_OWNED_ONLY],
        examples: [{ input: { intent: 'Triage a new GitHub issue' } }],
        public: true,
    },
    {
        name: 'os.list_tool_bindings',
        family: 'os.planning',
        summary: 'The tool types this enterprise has bound, at which layer, and the tools each exposes.',
        description:
            'List the tool types this enterprise has bound, the layer each is bound at, and the individual tools each one exposes, asked of its MCP server. A proposal may only name tools of a bound type. A server that will not answer lists no tools, and its binding is still reported.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        needs: { binding: "Each binding's own credential, to ask its server which tools it exposes." },
        errors: [OS_OWNED_ONLY],
        examples: [{ input: {} }],
        public: true,
    },
    {
        // #1013: the other half of what a task can be given. os.list_tool_bindings answers for vendor tools; this
        // answers for the OS's own, read off this registry with the predicate the save-time definition check
        // applies, so a designing run proposes only names the save accepts.
        name: 'os.list_built_in_tools',
        family: 'os.planning',
        summary: 'Every built-in tool a run can call, with its summary and whether it reads or writes.',
        description:
            'List every built-in tool a run can call: its name, a one-line summary, and its effect (read or write). A task may declare a built-in tool (os.*, workspace.*, memory.*, web.*) only from this list: saving a task that names any other is refused, as a tool that does not exist (tool_unknown) or one only the CEO chat offers (tool_not_run_callable). A row with gate is a tool a task may hold only if it also declares a CEO gate on the tool gate names: saving it without that gate is refused (gate_required_for_tool). Vendor tools are not here: os.list_tool_bindings lists what the enterprise has bound. Takes no arguments.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns:
            '`[{ name, summary, effect, gate? }]`: one row per built-in tool a run can call, in reference order. `gate` is only on a tool a task may hold only with a CEO gate, and names the tool that gate is declared on.',
        callers: ['run', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [OS_OWNED_ONLY],
        // The same rows over the API and MCP: `callable=run` narrows the reference endpoint to this listing.
        api: { method: 'GET', path: '/v1/tools/built-in?callable=run', scope: 'tools:read' },
        examples: [{ input: {} }],
        public: true,
    },
    {
        name: 'os.read_task',
        family: 'os.planning',
        summary: 'One task in full, its pending recommendations, and the tasks that start it.',
        description:
            'One task in full (as os.read_enterprise reads a task), the improvement recommendations still pending against it, and incomingSuccessors: the tasks whose successors start it. Its own successors are what it starts. The task also carries currentVersionId, the version this read is of: an update_task item you write from it names that as its baseVersionId. Pass taskId.',
        inputSchema: {
            type: 'object',
            properties: { taskId: { type: 'string' } },
            required: ['taskId'],
            additionalProperties: false,
        },
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        errors: [
            OS_OWNED_ONLY,
            { code: 'task_id_required', when: '`taskId` is missing.', handling: 'tool_error' },
            { code: 'task_not_found', when: 'No task in this enterprise has that id.', handling: 'tool_error' },
        ],
        examples: [{ input: { taskId: '5f0c1d2e-0000-4000-8000-000000000001' } }],
        public: true,
    },
    {
        name: 'os.assess_impact',
        family: 'os.planning',
        summary: 'What points at a role, team, task or member a plan would delete.',
        description:
            'What points at a role, team, task or member a plan would delete. role: the members holding it and the tasks assigned to it. team: the roles held on it and the members on it; the tasks it runs belong to those roles and stay with their holders, so only its roster stands in the way of deleting it. task: the tasks whose successors name it and its runs in flight. member: the roles they hold, the teams they sit on and the tasks their roles run. Reported, never refused: the plan says what the deletion breaks. Pass kind and id.',
        inputSchema: {
            type: 'object',
            properties: {
                kind: { type: 'string', enum: ['role', 'team', 'task', 'member'] },
                id: { type: 'string' },
            },
            required: ['kind', 'id'],
            additionalProperties: false,
        },
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        errors: [
            OS_OWNED_ONLY,
            { code: 'id_required', when: '`id` is missing.', handling: 'tool_error' },
            { code: 'kind_invalid', when: '`kind` is not role, team, task or member.', handling: 'tool_error' },
        ],
        examples: [{ input: { kind: 'role', id: '5f0c1d2e-0000-4000-8000-000000000002' } }],
        public: true,
    },
    {
        name: 'os.write_plan',
        family: 'os.planning',
        summary: "Write or replace this conversation's plan, checked before the CEO sees it.",
        description:
            "Write this conversation's plan for the CEO, or replace it: one plan per conversation, and a replace carries the version os.read_plan returned. The conversation is the chatSessionId you pass when you have one, else the planKey in your run's input, which the OS fills with the first proposing run's id and every later round carries, so you never pass planKey yourself. Every item is checked first, and the first problem comes back as the reason: a tool whose type this enterprise has not bound, a slug it already has, a task pinned to a member (set assigneeRole), a task placed on a team (a task belongs to a role: drop teamId and teamHandle and seat a holder of its role on the team instead), a task whose role nobody holds and no item in the plan gives to anyone, a task definition a saved task would fail, or an update_task item whose task has moved on from the version it names. An update_task item names the version it was written from as baseVersionId: the currentVersionId os.read_task returned. One that names none is given the version current as the plan is written. Fix it and write again. Pass summary, ceoIntent, kind (default os_change), items, the chatSessionId from your input when you have one and, to replace, version.",
        inputSchema: {
            type: 'object',
            properties: {
                chatSessionId: { type: 'string' },
                summary: { type: 'string' },
                ceoIntent: { type: 'string' },
                kind: { type: 'string' },
                items: { type: 'array', items: { type: 'object' } },
                version: { type: 'integer', minimum: 1 },
            },
            required: ['summary', 'items'],
            additionalProperties: false,
        },
        callers: ['run'],
        effect: 'write',
        sideEffects: 'Moves the plan to `awaiting_ceo`.',
        retry: 'idempotent',
        retryNote:
            'A repeat replaces the same plan with the same content rather than adding a second. A repeat carrying a version the plan has moved past is refused as `plan_version_conflict`.',
        errors: [
            OS_OWNED_ONLY,
            {
                code: 'plan_key_required',
                when: 'No `chatSessionId` was passed and the run carries no `planKey` in its input, so the plan has no conversation to belong to.',
                handling: 'tool_error',
            },
            { code: 'summary_required', when: '`summary` is missing.', handling: 'tool_error' },
            {
                code: 'plan_payload_invalid',
                when: 'An item is not one the plan can apply (a missing handle, an unknown type).',
                handling: 'tool_error',
            },
            {
                code: 'tool_type_not_bound:<type>',
                when: 'An item names a tool whose type this enterprise has not bound.',
                handling: 'tool_error',
            },
            {
                code: 'slug_collision:<slug>',
                when: 'An item creates a task, role or team with a slug the enterprise already has.',
                handling: 'tool_error',
            },
            {
                code: 'assignee_member_removed',
                when: 'A `create_task` item names `assigneeMemberId`. A task is assigned to a role: set `assigneeRole`.',
                handling: 'tool_error',
            },
            {
                code: 'task_team_removed:<slug>',
                when: 'A `create_task` or `update_task` item names `teamId` or `teamHandle`. A task belongs to a role, not a team: a team runs the tasks of the roles its members hold. Seat a holder of the task’s role on the team instead.',
                handling: 'tool_error',
            },
            {
                code: 'role_unplaced:<role>',
                when: 'A task runs as a role nobody holds, and no item in the plan creates a member who holds it, so it would never start.',
                handling: 'tool_error',
            },
            {
                code: 'definition_issue:<code>',
                when: 'A proposed task fails the check a saved task must pass (an unknown successor, a deterministic task with tools).',
                handling: 'tool_error',
            },
            {
                code: 'plan_version_conflict',
                when: '`version` is not the version the plan is at now. Read it back with os.read_plan and write again.',
                handling: 'tool_error',
            },
            {
                code: 'task_version_conflict',
                when: "An `update_task` item's `baseVersionId` is not its task's current version: someone published since you read it. The reason names the current version. Read the task again with os.read_task and write the change against that.",
                handling: 'tool_error',
            },
        ],
        examples: [
            {
                input: {
                    chatSessionId: 'chat-42',
                    ceoIntent: 'Hire a support lead',
                    summary: 'Creates a Support Lead role',
                    kind: 'os_change',
                    items: [{ type: 'create_role', handle: 'r1', slug: 'support-lead', name: 'Support Lead' }],
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.read_plan',
        family: 'os.planning',
        summary: "This conversation's plan as it stands, with its version.",
        description:
            "Read this conversation's plan as it stands, with the version a replacing os.write_plan must carry, so a feedback round continues it rather than starting again. The conversation is the chatSessionId you pass when you have one, else the planKey in your run's input, which every round of the conversation carries. Null when the conversation has no plan. With neither, the plan this run wrote.",
        inputSchema: {
            type: 'object',
            properties: { chatSessionId: { type: 'string' } },
            additionalProperties: false,
        },
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        errors: [OS_OWNED_ONLY],
        examples: [{ input: { chatSessionId: 'chat-42' } }],
        public: true,
    },
]
